import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import classnames from 'classnames'
import { useMediaQuery } from 'react-responsive'

import Page from '../index'
import SettingsNavMenu from './SettingsNavMenu'
import { TabletDown, TabletUp } from '../../components/hoc/ResponsiveRendering'
import EditProfile from './EditProfile'
import Partnerships from './Partnerships'
import ManageTeam from './ManageTeam'
import PagesAndIdentities from './PagesAndIdentities'
import Margins from './Margins'
import PartnerAccounts from './PartnerAccounts'
import ManageLocationLists from './ManageLocationLists'
import Help from './Help'
import OnSiteAdServer from './OnSiteAdServer'
import RightHolderProfile from './RightHolderProfile'
import Audiences from './Audiences'

import usePermissions from '../../hooks/usePermissions'
import { redirectTo } from '../../helpers/url'

import { getCurrentUserSelector } from '../../modules/selectors/app'
import { selectedPartnershipSelector } from '../../modules/selectors/partnerships'
import { selectedFacebookAdAccountSelector, selectedTikTokAdAccountSelector } from '../../modules/selectors/adAccounts'

import { ROUTES, SETTINGS_ROUTES } from '../../constants/routes'
import { ON_SITE_AD_SERVER_SETTINGS_PAGE, RIGHT_HOLDER_ACCOUNT_PROFILE } from '../../constants/permissions'

import { tabletDownSize } from '../../styles/const/breakpoints'
import useStyles from './styles'

function Settings() {
  const { t } = useTranslation()
  const params = useParams()

  const [isMenuOpened, setIsMenuOpened] = useState(true)
  const [isMenuSwiped, setIsMenuSwiped] = useState(false)
  const [isSectionPreOpened, setIsSectionPreOpened] = useState(false)

  const classes = useStyles()

  const selectedPartnership = useSelector(selectedPartnershipSelector)
  const { first_name: firstName } = useSelector(getCurrentUserSelector)
  const { id: facebookAdAccountId } = useSelector(selectedFacebookAdAccountSelector)
  const { id: tikTokAdAccountId } = useSelector(selectedTikTokAdAccountSelector)

  const {
    rights_holder_name: rightsHolderName,
    partner: partnerId,
    rights_holder: rightsHolderId,
    partner_name: partnerName
  } = selectedPartnership

  const permissions = usePermissions()

  const subPagePath = params.settingSection

  function isSelectedSetting(section) {
    return section.pathParam === subPagePath
  }

  const showPagesAndIdentitiesSettingsPage =
    permissions.can('manage', 'SettingsPagesAndIdentities') && (!!facebookAdAccountId || !!tikTokAdAccountId)

  const showSettingsRightsHolderSection = permissions.can('manage', 'SettingsRightsHolderSection')

  const closeMenu = () => {
    setIsMenuSwiped(true)
    setTimeout(() => {
      setIsMenuOpened(false)
    }, 450)
  }

  const openMenu = useCallback(() => {
    setIsMenuOpened(true)
    setTimeout(() => {
      setIsMenuSwiped(false)
    }, 0)
  }, [setIsMenuOpened, setIsMenuSwiped])

  const isTablet = useMediaQuery({ maxWidth: tabletDownSize })

  const partnerSettings = [
    {
      component: <ManageTeam selfAccountId={partnerId} />,
      path: ROUTES.managePartnerTeam,
      pathParam: SETTINGS_ROUTES.MANAGE_PARTNER_TEAM,
      title: t('Team'),
      breadcrumb: t(`${partnerName} Team`),
      show: permissions.can('manage', 'SettingsPartnerTeam')
    },
    {
      component: <ManageLocationLists />,
      path: ROUTES.managePresets,
      pathParam: SETTINGS_ROUTES.MANAGE_LOCATION_LISTS,
      title: t('Location List'),
      show: permissions.can('manage', 'SettingsLocationLists')
    },
    {
      component: <PagesAndIdentities />,
      path: ROUTES.pagesAndIdentities,
      pathParam: SETTINGS_ROUTES.PAGES_AND_IDENTITIES,
      title: t('Pages and Identities'),
      show: showPagesAndIdentitiesSettingsPage
    },
    {
      component: <Audiences />,
      path: ROUTES.audiences,
      pathParam: SETTINGS_ROUTES.AUDIENCES,
      title: t('Audiences'),
      show: true
    },
    {
      component: <Margins />,
      path: ROUTES.settingsMargins,
      pathParam: SETTINGS_ROUTES.MARGINS,
      title: t('Margins'),
      show: permissions.can('manage', 'SettingsMargins')
    },
    {
      component: <OnSiteAdServer />,
      path: ROUTES.onSiteAdServer,
      pathParam: SETTINGS_ROUTES.ON_SITE_AD_SERVER,
      title: t('Onsite Ad Server'),
      show: permissions.can('read', ON_SITE_AD_SERVER_SETTINGS_PAGE)
    }
  ]

  const rightsHolderSettings = [
    {
      component: <ManageTeam selfAccountId={rightsHolderId} isRightsHolderTeam />,
      path: ROUTES.settings,
      title: t('Team'),
      breadcrumb: t(`${rightsHolderName} Team`),
      show: permissions.can('manage', 'RightsTeamManage')
    },
    {
      component: <PartnerAccounts />,
      path: ROUTES.partnerAccounts,
      pathParam: SETTINGS_ROUTES.PARTNER_ACCOUNTS,
      title: t('Partner Accounts'),
      show: permissions.can('manage', 'PartnerAccounts')
    },
    {
      component: <Partnerships />,
      path: ROUTES.settingsPartnerships,
      pathParam: SETTINGS_ROUTES.PARTNERSHIPS,
      title: t('Partnerships'),
      show: permissions.can('manage', 'SettingsPartnerships')
    },
    {
      component: <RightHolderProfile />,
      path: ROUTES.rightHolderProfile,
      pathParam: SETTINGS_ROUTES.RIGHT_HOLDER_PROFILE,
      title: t('Profile'),
      show: permissions.can('manage', RIGHT_HOLDER_ACCOUNT_PROFILE)
    }
  ]

  const userSettings = [
    {
      component: <EditProfile />,
      path: ROUTES.myInformation,
      pathParam: SETTINGS_ROUTES.MY_INFORMATION,
      title: t('User Profile'),
      show: true
    },
    {
      component: <Help />,
      path: ROUTES.help,
      pathParam: SETTINGS_ROUTES.HELP,
      title: t('Help'),
      show: true
    }
  ]

  const visiblePartnerSettings = partnerSettings.filter(item => item.show)
  const visibleRightsHolderSettings = rightsHolderSettings.filter(item => item.show)
  const visibleUserSettings = userSettings.filter(item => item.show)

  const allVisibleSections = [...visiblePartnerSettings, ...visibleRightsHolderSettings, ...visibleUserSettings]
  const selectedRouteSettingSection = allVisibleSections.find(isSelectedSetting)
  const selectedSection = selectedRouteSettingSection || allVisibleSections[0]

  const breadcrumbs = useMemo(
    () => [
      {
        title: 'Settings'
      },
      {
        title: selectedSection.breadcrumb || selectedSection.title
      }
    ],
    [selectedSection.breadcrumb, selectedSection.title]
  )

  useEffect(() => {
    // redirect to EditProfile section if the selected section wasn't find(could miss show permissions for example)
    if (!selectedRouteSettingSection && subPagePath) {
      redirectTo(ROUTES.myInformation)
    }
  }, [selectedRouteSettingSection, subPagePath])

  useEffect(() => {
    // pre-open section content if page is not index (have subPath)
    // it helps to prevent behaviour when navigation menu is always shown on mobile as first view
    if (subPagePath && !isSectionPreOpened) {
      setIsMenuSwiped(true)
      setIsMenuOpened(false)
      setIsSectionPreOpened(true)
    } else if (!subPagePath && !isSectionPreOpened) {
      setIsSectionPreOpened(true)
    }
  }, [isSectionPreOpened, subPagePath])

  return (
    <Page
      helmetTitle="Settings"
      className={classes.page}
      breadcrumbs={breadcrumbs}
      onBackButtonClick={isTablet ? openMenu : null}
      showBackBtn={isMenuOpened}
      back
    >
      <div className={classes.settingsPage}>
        <TabletUp>
          <section className={classes.navContainer}>
            <SettingsNavMenu
              settings={visiblePartnerSettings}
              subPagePath={subPagePath}
              sectionName={`${partnerName} account settings`}
            />
            {showSettingsRightsHolderSection && (
              <SettingsNavMenu
                settings={visibleRightsHolderSettings}
                subPagePath={subPagePath}
                sectionName={`${rightsHolderName} account settings`}
              />
            )}
            <SettingsNavMenu
              settings={visibleUserSettings}
              subPagePath={subPagePath}
              sectionName={`${firstName}'s settings `}
            />
          </section>
          <div className={classes.settingsContent}>
            <div>{selectedSection.component}</div>
          </div>
        </TabletUp>

        <TabletDown>
          {isMenuOpened ? (
            <section className={classnames(classes.navContainer, isMenuSwiped ? 'swiped' : '')}>
              <SettingsNavMenu
                settings={visiblePartnerSettings}
                subPagePath={subPagePath}
                closeMenu={closeMenu}
                sectionName={`${partnerName} account settings`}
              />
              {showSettingsRightsHolderSection && (
                <SettingsNavMenu
                  settings={visibleRightsHolderSettings}
                  subPagePath={subPagePath}
                  closeMenu={closeMenu}
                  sectionName={`${rightsHolderName} account settings`}
                />
              )}
              <SettingsNavMenu
                settings={visibleUserSettings}
                subPagePath={subPagePath}
                closeMenu={closeMenu}
                sectionName={`${firstName}'s settings `}
              />
            </section>
          ) : (
            <div className={classes.settingsContent}>{selectedSection.component}</div>
          )}
        </TabletDown>
      </div>
    </Page>
  )
}

export default Settings
